/**
 * WEB-735: мутационные ответы BE (rename/publish/unpublish/activate…)
 * приходят БЕЗ ownerName — владелец батч-резолвится только в list-эндпоинтах
 * (WEB-727, by-design). Редьюсер, заменяющий строку списка ответом мутации,
 * обязан сохранить прежний ownerName, иначе колонка «Владелец» показывает
 * «—» до следующей полной загрузки.
 */
export function preserveOwnerName<T extends { ownerName?: string | null }>(
  prev: T,
  next: T,
): T {
  if (next.ownerName == null && prev.ownerName != null) {
    return { ...next, ownerName: prev.ownerName };
  }
  return next;
}

/**
 * WEB-1929: то же самое для состояний кусков воронки.
 *
 * `quizState`/`paywallState` считает ТОЛЬКО список воронок (сервер собирает их
 * одним запросом на страницу, `FunnelsService.findAll`). Ответ мутации
 * (переименование/публикация/снятие) приходит без них — и замена строки этим
 * ответом стирала бы признак «квиз удалён»: чип снова становился бы дверью в
 * конструктор на удалённый кусок, то есть ровно тем дефектом, из-за которого
 * заведена карточка. Тот же шрам, что WEB-735 у ownerName, поэтому и лечится
 * рядом с ним — список полей «считает только список» держится в одном месте.
 *
 * Наследуется прежнее состояние ТОЛЬКО при неизменившемся id куска: «Починить»
 * подключает воронке другой квиз, и старое DELETED про него — уже неправда.
 * `undefined` (ни там, ни там) остаётся `undefined` — «неизвестно», кабинет
 * ведёт себя как раньше и «удалён» не выдумывает.
 */
export function preserveFunnelListOnlyFields<
  T extends {
    ownerName?: string | null;
    quizId?: string | null;
    paywallId?: string | null;
    quizState?: unknown;
    paywallState?: unknown;
    /**
     * WEB-1983: число мест под картинку без картинки. Считает его тоже только
     * список воронок (по содержимому кусков), поэтому и переживать ответ
     * мутации оно обязано так же, как состояния кусков. Иначе пометка гасла бы
     * после переименования или публикации, а человек прочитал бы это как «дыры
     * починились», хотя не менялось ничего.
     */
    missingImageCount?: number;
  },
>(prev: T, next: T): T {
  const merged = preserveOwnerName(prev, next);
  const patch: Record<string, unknown> = {};

  if (
    merged.quizState === undefined &&
    prev.quizState !== undefined &&
    next.quizId === prev.quizId
  ) {
    patch.quizState = prev.quizState;
  }
  if (
    merged.paywallState === undefined &&
    prev.paywallState !== undefined &&
    next.paywallId === prev.paywallId
  ) {
    patch.paywallState = prev.paywallState;
  }
  // WEB-1983: число считается по СОДЕРЖИМОМУ ОБОИХ кусков сразу, поэтому
  // наследуется, только когда оба куска остались прежними: «Починить»
  // подключает воронке другой квиз или пейволл, и старое число — про другую
  // воронку.
  if (
    merged.missingImageCount === undefined &&
    prev.missingImageCount !== undefined &&
    next.quizId === prev.quizId &&
    next.paywallId === prev.paywallId
  ) {
    patch.missingImageCount = prev.missingImageCount;
  }

  return Object.keys(patch).length > 0 ? { ...merged, ...patch } : merged;
}
